import { useState } from 'react';

import { classifyAuthError } from '@/lib/auth/auth-error';

/**
 * An async action that is offered once, reports failure, and then says "done".
 *
 * Both status screens that have an action hand-rolled this: "Resend email" on
 * the confirm-your-email card and "Email me a recovery link" on the deactivated
 * card. They kept the identical `useState` triple - pending / done / error - and
 * the identical handler shape, and they still managed to diverge:
 * `AuthSuccess` kept the button mounted and swapped its label, while
 * `DeactivatedAccountCard` UNMOUNTED the button and moved the done-state into
 * the card body. Same interaction, two affordances, no recorded reason.
 *
 * They also disagreed on failure, and still have to: one calls Supabase, which
 * returns `{ error }`, and the other calls our own backend, which throws an
 * `ApiError`. `resolveError` is how each says what a failure means. It defaults
 * to `classifyAuthError` - the right answer for anything going to Supabase, and
 * what turns a 429 into "Try again in 53 seconds", which Resend is the most
 * likely button in the app to meet.
 *
 * It is a parameter rather than a hardcoded call because making it one revealed
 * the bug: routing the account-restore request through the auth classifier
 * printed a raw `HTTP 500` string to the user, which is the exact defect
 * `lib/account/restore-error.ts` was written to prevent.
 */
export function useOneShotAction(
  action: () => Promise<unknown>,
  resolveError: (err: unknown) => string = err =>
    classifyAuthError(err, 'login').message
) {
  const [isPending, setIsPending] = useState(false);
  const [isDone, setIsDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = async () => {
    setIsPending(true);
    setError(null);
    try {
      // Supabase returns `{ error }`; our own api-client throws. Handle both,
      // because these two screens are one of each.
      const result = (await action()) as { error?: unknown } | undefined;
      if (result && typeof result === 'object' && result.error) {
        setError(resolveError(result.error));
      } else {
        setIsDone(true);
      }
    } catch (err) {
      setError(resolveError(err));
    } finally {
      setIsPending(false);
    }
  };

  return { isPending, isDone, error, run };
}
